import { useMemo } from 'react';
import { Joyride, type Step } from 'react-joyride';
import { WELCOME_TOUR_STEPS, type TourStepDef } from './welcomeTourSteps';
import { TourTooltip } from './TourTooltip';
import type { useWelcomeTour } from '../../hooks/useWelcomeTour';

const esMovil = () =>
  globalThis.window !== undefined && globalThis.matchMedia('(max-width: 1023px)').matches;

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Qué hay que preparar antes de mostrar un paso, según dónde viva su objetivo.
 *
 * - `menu`: en teléfono el menú lateral está colapsado; hay que revelarlo.
 * - `contenido`: las fichas de Balance pueden quedar bajo el pliegue (en un teléfono de 375px
 *   la ficha de Aportes arranca en y≈863 con la pantalla arriba del todo). Como `skipScroll`
 *   deja el desplazamiento en nuestras manos, se acerca el objetivo a mano — y solo si de
 *   verdad quedó fuera, para no mover la pantalla en escritorio, donde ya se ve.
 */
function antesDelPaso(
  def: TourStepDef,
  onOpenMobileMenu: () => void,
): (() => Promise<void>) | undefined {
  if (def.tipo !== 'accionable') return undefined;

  if (def.objetivo === 'menu') {
    return async () => {
      if (esMovil()) {
        onOpenMobileMenu();
        await wait(320); // sidebar transition-duration-300
      }
    };
  }

  return async () => {
    const objetivo = await esperarObjetivo(def.target);
    if (!objetivo) return;
    const caja = objetivo.getBoundingClientRect();
    const fueraDeLaVentana = caja.top < 0 || caja.bottom > globalThis.innerHeight;
    if (!fueraDeLaVentana) return;
    objetivo.scrollIntoView({ block: 'center' });
    await wait(200); // que la posición se asiente antes de que Joyride mida el objetivo
  };
}

/**
 * Espera a que el objetivo exista en la página antes de seguir.
 *
 * Las fichas de Balance se dibujan recién cuando el balance terminó de cargar, así que quien
 * toca "Continuar" apenas aparece el paso anterior puede llegar acá con la ficha todavía
 * ausente. Joyride, ante un objetivo que no encuentra, **salta el paso sin avisar**: el
 * recorrido pasaba de "Balance" directo al mensaje de llegada a Aportes, sin mostrar nunca
 * el paso que pide tocar la ficha. Falla en silencio, así que solo se ve contando los pasos.
 */
async function esperarObjetivo(selector: string, msMaximos = 4000): Promise<Element | null> {
  const limite = Date.now() + msMaximos;
  let objetivo = document.querySelector(selector);
  while (!objetivo && Date.now() < limite) {
    await wait(100);
    objetivo = document.querySelector(selector);
  }
  return objetivo;
}

interface WelcomeTourProps {
  /** Estado del tour (el hook lo posee Layout para compartirlo con TopBar). */
  readonly tour: ReturnType<typeof useWelcomeTour>;
  /** Abre el menú lateral en móvil (para pasos accionables). */
  readonly onOpenMobileMenu: () => void;
}

export function WelcomeTour({ tour, onOpenMobileMenu }: WelcomeTourProps) {
  const steps: Step[] = useMemo(
    () =>
      WELCOME_TOUR_STEPS.map((def, i) => ({
        target: def.target,
        content: def.contenido,
        title: def.titulo,
        placement: def.placement,
        data: def, // el tooltip lee tipo/labels desde aquí
        skipBeacon: true,
        // "Atrás" en cualquier paso salvo el primero y salvo los checkpoints de llegada: un
        // checkpoint no es un punto de decisión, y "Atrás" no reversa la navegación —
        // volver desde ahí al paso accionable anterior mostraría "toca aquí" sobre una
        // ruta en la que el usuario ya está parado (confuso, bug real reportado).
        // En pasos accionables solo "skip" (+ back); en informativos/llegada primary + skip (+ back).
        buttons: [
          ...(i > 0 && def.tipo !== 'llegada' ? ['back'] : []),
          ...(def.tipo === 'accionable' ? [] : ['primary']),
          'skip',
        ] as Step['buttons'],
        // Móvil: revelar el menú antes de resaltar el ítem, solo cuando el objetivo vive ahí.
        // Contenido: acercar el objetivo si quedó fuera de la ventana (ver `skipScroll` abajo).
        before: antesDelPaso(def, onOpenMobileMenu),
      })),
    [onOpenMobileMenu],
  );

  return (
    <Joyride
      steps={steps}
      run={tour.run}
      stepIndex={tour.stepIndex}
      continuous
      onEvent={tour.onEvent}
      tooltipComponent={TourTooltip}
      options={{
        overlayClickAction: false,      // clic en overlay NO cierra (encarrilado)
        dismissKeyAction: 'close',      // ESC cierra (a11y) → se trata como skip
        blockTargetInteraction: false,  // el ítem resaltado SÍ recibe el clic real
        spotlightPadding: 6,
        spotlightRadius: 10,
        overlayColor: 'rgba(15, 23, 42, 0.5)', // slate-900/50, como los overlays del proyecto
        primaryColor: '#2563eb',        // blue-600 (por si algún default lo usa)
        zIndex: 100,                    // sobre header (z-40), sidebar (z-30), dropdowns (z-50)
        disableFocusTrap: true,         // el modelo accionable necesita alcanzar el ítem del menú
        targetWaitTimeout: 3000,        // espera a que la página/target monte antes de fallar suave
        // El auto-scroll-to-target de Joyride rompía el `position: sticky` del header/sidebar de
        // forma persistente tras la primera navegación (verificado con Playwright), así que se
        // apaga. **Ojo con la premisa**: esto nació cuando todos los objetivos eran ítems del
        // menú o de la barra superior, siempre visibles por ser fijos, y decía que "nunca hace
        // falta scrollear". Dejó de ser cierto al mover Aportes y Movimientos a fichas dentro
        // del contenido de Balance: en teléfono quedan bajo el pliegue. El desplazamiento que
        // esos pasos necesitan lo hace `antesDelPaso()`, acotado a los objetivos de contenido
        // que estén fuera de la ventana — no se reactiva el de Joyride.
        skipScroll: true,
      }}
    />
  );
}
